import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Stethoscope, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, remember);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-gradient-to-br from-emerald-950 via-[#07231d] to-slate-950 p-12 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 bg-emerald-500/25 rounded-full blur-3xl animate-floaty" />
        <div className="absolute bottom-0 -left-24 h-96 w-96 bg-emerald-600/15 rounded-full blur-3xl" />
        <div className="flex items-center gap-3 relative z-10">
          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/40">
            <Stethoscope className="h-6 w-6 text-white" />
          </div>
          <span className="font-display font-extrabold text-white text-2xl tracking-tight">ZAP DOUTOR</span>
        </div>
        <div className="relative z-10">
          <h1 className="font-display text-4xl font-bold text-white leading-tight">
            CRM da sua clínica <br /> + <span className="text-emerald-400">Disparos em massa</span> no WhatsApp
          </h1>
          <p className="text-emerald-100/60 mt-4 max-w-md">
            Gerencie clientes, organize com tags e envie mensagens em massa com controle total. Simples, rápido e profissional.
          </p>
        </div>
        <p className="text-emerald-200/30 text-xs relative z-10">Acesso restrito ao administrador • Dados protegidos (LGPD)</p>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-transparent">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="h-10 w-10 rounded-2xl bg-emerald-500 flex items-center justify-center">
              <Stethoscope className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-extrabold text-slate-900 text-xl">ZAP DOUTOR</span>
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Acesso administrativo</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">Entre com suas credenciais para continuar.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">E-mail ou usuário</Label>
              <Input
                id="email" type="text" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="seu e-mail ou usuário" required className="mt-1.5" data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Senha</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password" type={show ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required
                  className="pr-10" data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="login-remember-checkbox" />
                Lembrar acesso
              </label>
              <Link to="/forgot-password" className="text-sm text-emerald-600 hover:underline font-medium" data-testid="forgot-password-link">
                Recuperar senha
              </Link>
            </div>
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-600 h-11 text-base" data-testid="login-submit-button">
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
